import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { caseService, projectService } from '../services/api';
import ProjectWizard from '../components/ProjectWizard';
import CaseOverview from '../components/CaseOverview';
import CaseNotes from '../components/CaseNotes';
import CollaborationStatus from '../components/CollaborationStatus';
import StrategyMapping from '../components/StrategyMapping';
import StrategicEnvironment from '../components/StrategicEnvironment';
import Backcasting from '../components/Backcasting';
import Chat from '../components/Chat';
import useCollaboration from '../hooks/useCollaboration';

const CaseDetail = () => {
  const { t } = useTranslation();
  const { workspaceId, caseId } = useParams();
  const { currentUser } = useAuth();
  const [case_, setCase] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('strategic-environment');
  const [showChat, setShowChat] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  
  // Real-time collaboration
  const collaboration = useCollaboration(caseId);

  // Report presence when tab changes (only if connected)
  useEffect(() => {
    if (collaboration.reportPresence && collaboration.isConnected && activeTab) {
      collaboration.reportPresence(activeTab);
    }
  }, [activeTab, collaboration.reportPresence, collaboration.isConnected]);

  useEffect(() => {
    fetchCaseAndWorkspace();
  }, [workspaceId, caseId]);

  const fetchCaseAndWorkspace = async () => {
    try {
      const [caseData, workspaceData] = await Promise.all([
        caseService.getCase(caseId),
        projectService.getProject(workspaceId)
      ]);
      setCase(caseData);
      setWorkspace(workspaceData);
    } catch (error) {
      toast.error(t('cases.fetchError'));
      console.error('Error fetching case:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewMessage = (messageData) => {
    
    // Only count as unread if chat is closed and message is from another user
    if (!showChat && messageData.user_id !== currentUser?.id) {
      setUnreadMessages(prev => {
        const newCount = prev + 1;
        return newCount;
      });
    }
  };

  const handleChatOpen = () => {
    setShowChat(true);
    // Clear unread count when chat is opened
    setUnreadMessages(0);
  };

  const handleChatClose = () => {
    setShowChat(false);
  };

  // Listen for WebSocket chat messages directly in CaseDetail
  useEffect(() => {
    const handleChatMessage = (event) => {
      const messageData = event.detail;
      
      // Handle the message with current state values
      if (!showChat && messageData.user_id !== currentUser?.id) {
        setUnreadMessages(prev => {
          const newCount = prev + 1;
          return newCount;
        });
      }
    };

    window.addEventListener('collaboration-chat-message', handleChatMessage);
    
    return () => {
      window.removeEventListener('collaboration-chat-message', handleChatMessage);
    };
  }, [showChat, currentUser?.id]); // Dependencies to ensure proper state reference

  const handleCaseUpdate = (updatedCase) => {
    setCase(updatedCase);
  };

  if (loading) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center">{t('common.loading')}</div>
      </div>
    );
  }

  if (!case_ || !workspace) {
    return (
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center text-error-600">{t('cases.notFound')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Case Header */}
      <div className="bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumb */}
          <nav className="flex items-center text-sm text-neutral-500 mb-4" aria-label="Breadcrumb">
            <Link to="/workspaces" className="hover:text-neutral-700 transition-colors">
              {t('workspaces.title')}
            </Link>
            <svg className="mx-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <Link to={`/workspaces/${workspaceId}`} className="hover:text-neutral-700 transition-colors truncate max-w-48">
              {workspace.name}
            </Link>
            <svg className="mx-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-neutral-900 font-medium">{case_.name}</span>
          </nav>

          {/* Case Title & Metadata */}
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <h1 className="text-2xl font-semibold text-neutral-900">{case_.name}</h1>
                <div className="flex items-center gap-2">
                  {case_.is_archived && (
                    <span className="inline-flex items-center rounded-full bg-neutral-100 px-2.5 py-0.5 text-xs font-medium text-neutral-700">
                      {t('common.archived')}
                    </span>
                  )}
                  <span className="inline-flex items-center rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-medium text-accent-700 capitalize">
                    {case_.case_type && t(`cases.types.${case_.case_type}`, case_.case_type?.replace('_', ' '))}
                  </span>
                </div>
              </div>
              
              <div className="flex items-center gap-6 text-sm text-neutral-600">
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{case_.time_horizon || t('common.notSpecified')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span>{case_.category_system}</span>
                </div>
              </div>
            </div>
            
            <div className="flex-shrink-0">
              <CollaborationStatus
                isConnected={collaboration.isConnected}
                activeUsers={collaboration.activeUsers}
                activeEditors={collaboration.activeEditors}
                connectionError={collaboration.connectionError}
              />
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Amb sis pestanyes i etiquetes en català, la barra ja no hi cap en
              pantalles estretes: sense `overflow-x-auto` les últimes queden
              tallades i no s'hi arriba de cap manera. */}
          <nav className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('strategic-environment')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === 'strategic-environment'
                  ? 'border-accent-500 text-accent-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {t('cases.strategicEnvironment')}
            </button>
            <button
              onClick={() => setActiveTab('strategy-mapping')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === 'strategy-mapping'
                  ? 'border-accent-500 text-accent-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
              {t('cases.strategyMapping')}
            </button>
            <button
              onClick={() => setActiveTab('scenario-building')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === 'scenario-building'
                  ? 'border-accent-500 text-accent-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {t('cases.scenarioBuilding')}
            </button>
            <button
              onClick={() => setActiveTab('overview')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === 'overview'
                  ? 'border-accent-500 text-accent-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              {t('cases.overview')}
            </button>
            <button
              onClick={() => setActiveTab('backcasting')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === 'backcasting'
                  ? 'border-accent-500 text-accent-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              }`}
            >
              {/* Una fletxa que torna enrere: és exactament el que fa la tècnica. */}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6M3 10l6-6" />
              </svg>
              {t('cases.backcasting')}
            </button>
            <button
              onClick={() => setActiveTab('notes')}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                activeTab === 'notes'
                  ? 'border-accent-500 text-accent-600'
                  : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:border-neutral-300'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              {t('cases.notes')}
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeTab === 'strategic-environment' && (
            <StrategicEnvironment case_={case_} onUpdate={fetchCaseAndWorkspace} collaboration={collaboration} />
          )}
          
          {activeTab === 'strategy-mapping' && (
            <StrategyMapping case_={case_} onUpdate={fetchCaseAndWorkspace} collaboration={collaboration} />
          )}
          
          {activeTab === 'scenario-building' && (
            <ProjectWizard
              project={case_}
              onProjectUpdate={handleCaseUpdate}
              collaboration={collaboration}
              /* Acabar l'assistent porta al resum del cas, que és on es veu la
                 feina feta i des d'on es projecta. No marca cap estat nou. */
              onFinish={() => setActiveTab('overview')}
            />
          )}
          
          {activeTab === 'overview' && (
            <CaseOverview case_={case_} workspace={workspace} />
          )}
          
          {activeTab === 'backcasting' && (
            <Backcasting case_={case_} collaboration={collaboration} />
          )}

          {activeTab === 'notes' && (
            <CaseNotes case_={case_} />
          )}
        </div>
      </div>

      {/* Floating Chat Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={handleChatOpen}
          className="bg-accent-600 hover:bg-accent-700 text-white p-4 rounded-full shadow-lg transition-all duration-200 hover:shadow-xl relative"
          title={t('cases.chat')}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          {/* Notification indicator - show when there are unread messages */}
          {unreadMessages > 0 && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-xs text-white font-bold">{unreadMessages > 9 ? '9+' : unreadMessages}</span>
            </div>
          )}
        </button>
      </div>

      {/* Chat Window */}
      {showChat && (
        <div className="fixed bottom-20 right-6 z-50">
          <div className="bg-white rounded-lg shadow-2xl w-80 h-96 flex flex-col border border-gray-200">
            <div className="flex items-center justify-between p-3 border-b bg-accent-600 text-white rounded-t-lg">
              <div>
                <h3 className="text-sm font-semibold">{t('cases.chat')}</h3>
                <p className="text-xs text-accent-100 mt-0.5">{t('cases.chatDescription')}</p>
              </div>
              <button
                onClick={handleChatClose}
                className="text-accent-100 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <Chat caseId={case_.id} onNewMessage={handleNewMessage} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseDetail;